import type { ComponentChildren, JSX } from 'preact';
import { useState } from 'preact/hooks';
import { legacyPageToBlocks } from './legacy-page-to-blocks.js';
import { BlockRenderer } from './BlockRenderer.js';
import {
  type CheckoutBlockDto,
  type CheckoutNodeDto,
  type CheckoutPageConfigDto,
  pickLocale,
} from './api.js';

/**
 * Sprint E — single render entry point for `page`-kind flow nodes.
 *
 * Replaces the runner's previous `PageForm` / `ConfirmGate` / `ReturnPage`
 * switch with a uniform pipeline:
 *
 *   1. Run `legacyPageToBlocks(node.page)` so the rest of the function
 *      sees the new block-canvas shape, regardless of how the operator
 *      authored the page.
 *   2. Short-circuit to `ReturnPage` when `submit.action === 'cancel'`
 *      (the result-page render path) — `ReturnPage` is a self-contained
 *      composition that doesn't fit BlockRenderer's slots-map model.
 *   3. Build a `slots` map for `BlockRenderer`:
 *        - `lookup-form` slot → inline form populated from
 *          `page.formFieldIds`.
 *        - `pay-button` slot → operator-authored Continue button
 *          driving the page's `submit.action`.
 *        Anchor slots the page doesn't mention render nothing (the
 *        BlockRenderer falls through to `null`).
 *   4. Render `<BlockRenderer blocks={config.blocks} slots={slots}
 *      locale={locale} />`.
 *
 * Three Cs:
 *   - Configurable: every page renders from operator-authored blocks +
 *     form fields + submit declaration — no hardcoded layouts left in
 *     the runner.
 *   - Composable: `BlockRenderer` is the single page rendering surface;
 *     anchor slots are filled by the runner without BlockRenderer
 *     knowing the runner exists.
 *   - Conversational: the slot map keys are stable canonical block
 *     types — an audit reader knows what each rendered element is.
 */

/**
 * Pure-data summary of the slot map renderPageNode builds. Exposed for
 * tests so we can assert the slot keys without rendering Preact.
 */
export interface PageNodeSlotPlan {
  /** The block list (post-legacyPageToBlocks) the BlockRenderer will walk. */
  blocks: CheckoutBlockDto[];
  /** Which fields will be rendered in the `lookup-form` slot. */
  formFieldIds: (string | '*')[];
  /** Slot keys that will receive a non-empty slot. */
  filledSlotTypes: Array<'lookup-form' | 'pay-button'>;
  /** What pressing the submit button does. */
  submitAction: 'advance' | 'create_session' | 'cancel';
  /**
   * True when the page is a terminal/cancel page: `renderPageNode` renders
   * the operator-authored blocks (heading + markdown) without a form or
   * pay-button slot, and the runner does not advance from it. Historically
   * this routed through `ReturnPage` — the name is preserved for backwards
   * compatibility with the test plan.
   */
  rendersReturnPage: boolean;
}

/**
 * Compute the slot plan without rendering anything. Pure function — the
 * runner unit tests use this to assert the operator-visible behaviour
 * without spinning up the Preact tree.
 */
export function planPageNode(node: CheckoutNodeDto): PageNodeSlotPlan {
  const raw = (node.page ?? {}) as CheckoutPageConfigDto;
  const config = legacyPageToBlocks(raw);
  const submitAction = config.submit?.action ?? 'advance';
  const rendersReturnPage = submitAction === 'cancel';
  const blocks = (config.blocks ?? []) as CheckoutBlockDto[];
  const formFieldIds = config.formFieldIds ?? [];

  const filledSlotTypes: Array<'lookup-form' | 'pay-button'> = [];
  if (!rendersReturnPage) {
    if (blocks.some((b) => b.type === 'lookup-form')) filledSlotTypes.push('lookup-form');
    if (blocks.some((b) => b.type === 'pay-button')) filledSlotTypes.push('pay-button');
  }

  return { blocks, formFieldIds, filledSlotTypes, submitAction, rendersReturnPage };
}

/**
 * Render context the runner passes in. Captures everything renderPageNode
 * needs to advance the flow without it being aware of the runner's
 * internal state plumbing.
 */
export interface RenderPageNodeContext {
  locale: string;
  /** Current form values; seeds the inline form. */
  initialFormValues: Record<string, string>;
  /** Operator-authored help text under the form. */
  helpText?: string;
  /**
   * Strategy display submitLabel; used when the page does NOT override
   * `submit.label`.
   */
  fallbackSubmitLabel?: string;
  /**
   * Runtime scope made available to text blocks for `{{ scope.foo }}`
   * substitution. Threaded down through `BlockRenderer`. When absent,
   * blocks render their text literally — used by previews + tests.
   */
  scope?: Record<string, unknown>;
  /** Called when the shopper presses the page's submit button. */
  onSubmit: (values: Record<string, string>) => void;
}

/**
 * Render a `page`-kind node. Returns Preact JSX.
 */
export function renderPageNode(
  node: CheckoutNodeDto,
  ctx: RenderPageNodeContext,
): JSX.Element {
  const config = legacyPageToBlocks((node.page ?? {}) as CheckoutPageConfigDto);
  const submitAction = config.submit?.action ?? 'advance';
  const blocks = (config.blocks ?? []) as CheckoutBlockDto[];

  if (submitAction === 'cancel') {
    // Terminal page (e.g. `page-failure`, `page-not-found`, or a legacy
    // `layout: 'result'` page). The operator authored their own heading +
    // markdown describing what happened — render those blocks directly,
    // without a form or submit button. We deliberately do NOT route through
    // `ReturnPage`: the previous code defaulted the outcome to `'success'`
    // when no title was set, which painted a "Payment successful" banner on
    // failure pages (regression on the Amentum flow when integration-geocode
    // failed and the runner correctly routed to `page-failure`).
    return (
      <div
        class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-3"
        data-testid="terminal-page"
      >
        <BlockRenderer
          blocks={blocks}
          locale={ctx.locale}
          slots={{}}
          {...(ctx.scope ? { scope: ctx.scope } : {})}
        />
      </div>
    );
  }

  const formFieldIds = config.formFieldIds ?? [];
  const submitLabel =
    pickLocale(config.submit?.label, ctx.locale) ||
    ctx.fallbackSubmitLabel ||
    'Continue';

  const slots: Partial<Record<CheckoutBlockDto['type'], ComponentChildren>> = {};

  const hasLookupForm = blocks.some((b) => b.type === 'lookup-form');
  const hasPayButton = blocks.some((b) => b.type === 'pay-button');
  // Sprint F form-fields anchor — operator-authored block whose slot
  // is filled by the same input rendering as `lookup-form`. Without
  // this detection, a page that uses `form-fields` (the new shape)
  // renders heading + button but no inputs (regression on
  // ten_amentum_local · amentum-visa-mrv-default).
  const hasFormFields = blocks.some((b) => b.type === 'form-fields');

  // Form state is owned by the renderPageNode-level <PageForm/> container
  // when the page has a lookup-form / form-fields slot. When the page has
  // no form anchor (e.g. a confirm-style page that is data-only), the
  // pay-button slot submits an empty value map.
  if (hasLookupForm || hasPayButton || hasFormFields) {
    // `helpText` is omitted (not passed as `undefined`) when the runner
    // didn't supply one — TS exactOptionalPropertyTypes treats those as
    // distinct cases.
    const shellProps = {
      blocks,
      locale: ctx.locale,
      formFieldIds,
      initial: ctx.initialFormValues,
      submitLabel,
      onSubmit: ctx.onSubmit,
      renderForSlots: slots,
      hasLookupForm,
      hasFormFields,
      hasPayButton,
      ...(ctx.helpText ? { helpText: ctx.helpText } : {}),
      ...(ctx.scope ? { scope: ctx.scope } : {}),
    };
    return <PageFormShell {...shellProps} />;
  }

  // No form anchor + no pay button — page is purely informational. Render
  // the blocks without form state.
  return (
    <BlockRenderer
      blocks={blocks}
      locale={ctx.locale}
      slots={slots}
      {...(ctx.scope ? { scope: ctx.scope } : {})}
    />
  );
}

/**
 * Internal container that owns the form state so the lookup-form slot
 * and the pay-button slot share inputs. Wraps a single `<form>` so
 * pressing Enter inside an input triggers `onSubmit` exactly like a
 * native form.
 */
function PageFormShell(props: {
  blocks: CheckoutBlockDto[];
  locale: string;
  formFieldIds: (string | '*')[];
  initial: Record<string, string>;
  submitLabel: string;
  helpText?: string;
  /** Runtime scope used by text-block templating. Optional. */
  scope?: Record<string, unknown>;
  onSubmit: (values: Record<string, string>) => void;
  renderForSlots: Partial<Record<CheckoutBlockDto['type'], ComponentChildren>>;
  hasLookupForm: boolean;
  hasFormFields: boolean;
  hasPayButton: boolean;
}): JSX.Element {
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...props.initial }));

  // Resolve the field-id list. '*' is left to the runner today (Sprint F
  // wires DynamicForm) — for E, '*' degrades to the two anchor fields the
  // SPA already knew about so the Amentum flow continues to walk.
  const effectiveFieldIds = props.formFieldIds.includes('*')
    ? ['referenceNumber', 'email']
    : (props.formFieldIds.filter((id) => id !== '*') as string[]);

  function update(id: string, v: string): void {
    setValues((prev) => ({ ...prev, [id]: v }));
  }

  const slots: Partial<Record<CheckoutBlockDto['type'], ComponentChildren>> = {
    ...props.renderForSlots,
  };

  // Shared input list — populates either the `lookup-form` slot (legacy
  // anchor) or the `form-fields` slot (Sprint F operator-authored
  // anchor). Both consume the same renderer; the block list decides
  // which slot is actually used.
  const inputsBlock =
    props.hasLookupForm || props.hasFormFields ? (
      <div class="space-y-3">
        {effectiveFieldIds.map((id) => (
          <label key={id} class="block text-body-sm">
            <span class="block font-semibold text-on-surface mb-1 capitalize">
              {id.replace(/([A-Z])/g, ' $1').trim()}
            </span>
            <input
              type={id === 'email' ? 'email' : 'text'}
              value={values[id] ?? ''}
              onInput={(e) => update(id, (e.currentTarget as HTMLInputElement).value)}
              class="w-full px-2 py-1 border border-outline-variant rounded bg-surface"
            />
          </label>
        ))}
      </div>
    ) : null;

  if (props.hasLookupForm) slots['lookup-form'] = inputsBlock;
  if (props.hasFormFields) slots['form-fields'] = inputsBlock;

  if (props.hasPayButton) {
    slots['pay-button'] = (
      <div>
        <button
          type="submit"
          class="px-4 py-2 rounded bg-primary text-on-primary text-body-sm font-semibold"
        >
          {props.submitLabel}
        </button>
        {props.helpText && (
          <p class="text-body-sm text-on-surface-variant pt-1">{props.helpText}</p>
        )}
      </div>
    );
  }

  return (
    <form
      class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(values);
      }}
    >
      <BlockRenderer
        blocks={props.blocks}
        locale={props.locale}
        slots={slots}
        {...(props.scope ? { scope: props.scope } : {})}
      />
    </form>
  );
}

/**
 * Cast helper — the runner's DTO type and core's domain type are
 * structurally identical for `CheckoutPageConfig`, but TypeScript
 * doesn't widen across the package boundary.
 *
 * @internal Exported only so callers reusing the helper from app code
 * can keep their narrowing tight.
 */
export type { CheckoutPageConfigDto };
